/**
 * CNESResultsTable Component - Hospital results table
 */
import React from 'react';
import { Building2, Phone, Mail, Eye, ArrowUp, ArrowDown } from 'lucide-react';
import type { Hospital, SortConfig } from '../../hooks/useCNESConsulta';

interface CNESResultsTableProps {
  resultados: Hospital[];
  sortConfigs: SortConfig[];
  onSort: (key: keyof Hospital, event: React.MouseEvent) => void;
  onViewDetails: (hospital: Hospital) => void;
}

export const CNESResultsTable: React.FC<CNESResultsTableProps> = ({
  resultados,
  sortConfigs,
  onSort,
  onViewDetails,
}) => {
  const renderSortIcon = (key: keyof Hospital) => {
    const sortIndex = sortConfigs.findIndex((s) => s.key === key);

    if (sortIndex < 0) {
      return (
        <div className="flex flex-col text-gray-400">
          <ArrowUp size={12} />
          <ArrowDown size={12} className="-mt-1" />
        </div>
      );
    }

    const sort = sortConfigs[sortIndex];
    const priorityLabels = ['①', '②', '③'];

    return (
      <div className="flex items-center gap-1">
        {sort.direction === 'asc' ? (
          <ArrowUp size={16} className="text-green-600 dark:text-green-400" />
        ) : (
          <ArrowDown size={16} className="text-green-600 dark:text-green-400" />
        )}
        {sortConfigs.length > 1 && (
          <span className="text-xs font-bold text-green-600 dark:text-green-400">
            {priorityLabels[sortIndex]}
          </span>
        )}
      </div>
    );
  };

  if (resultados.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <p className="text-lg">Nenhum hospital encontrado</p>
        <p className="text-sm">Tente ajustar os filtros de busca</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              onClick={(e) => onSort('nome', e)}
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
            >
              <div className="flex items-center gap-2">
                Hospital
                {renderSortIcon('nome')}
              </div>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <Phone className="w-4 h-4 inline mr-1" />
              Contato
            </th>
            <th
              onClick={(e) => onSort('leitos', e)}
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
            >
              <div className="flex items-center gap-2">
                Leitos
                {renderSortIcon('leitos')}
              </div>
            </th>
            <th
              onClick={(e) => onSort('tipoEstabelecimento', e)}
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
            >
              <div className="flex items-center gap-2">
                Tipo / Gestão
                {renderSortIcon('tipoEstabelecimento')}
              </div>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {resultados.map((hospital) => (
            <tr key={hospital.cnes} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 text-sm">
                <div className="font-semibold text-gray-900 text-base">
                  {hospital.nome.replace(/^http:\/\/[^\s]+\s*/, '')}
                </div>
                <div className="text-gray-500 text-xs mt-1 space-y-0.5">
                  {hospital.cnpj && <div>CNPJ: {hospital.cnpj}</div>}
                  <div>{hospital.cidade} - {hospital.estado}</div>
                  {hospital.cnes && <div>Código CNES: {hospital.cnes}</div>}
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-900">
                <div className="flex items-center gap-1">
                  <Phone className="w-3 h-3 text-gray-400" />
                  <span className="text-xs">{hospital.telefone || '-'}</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                  <Mail className="w-3 h-3 text-gray-400" />
                  <span>{hospital.email || '-'}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                  {hospital.leitos || 0}
                </span>
              </td>
              <td className="px-6 py-4 text-sm text-gray-900">
                <div className="text-xs">{hospital.tipoEstabelecimento || '-'}</div>
                <div className="text-gray-500 text-xs mt-1">{hospital.gestao || '-'}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <button
                  onClick={() => onViewDetails(hospital)}
                  className="text-green-600 hover:text-green-900 transition-colors"
                  title="Ver detalhes completos"
                >
                  <Eye className="w-5 h-5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
